import axios from 'axios'
import { isEmpty } from 'lodash'
import { Address, AddressField, AddressSuggestion, LocalGovernmentArea, SelectedResult } from './arcgis-models'
export default class ArcGISAutocomplete {
    rootEl: HTMLDivElement
    inputEl: HTMLInputElement
    autocompleteInputEl: HTMLInputElement
    suggestionsList: HTMLUListElement
    radios: NodeListOf<HTMLDivElement>
    timeoutId: NodeJS.Timeout
    userId: string

    addressFields: AddressField;
    selectedAddress: Address;
    fieldsPopulated: boolean = false;

    hoveredIndex: number
    layerType: string

    constructor(rootElId: string, autocompleteInputElId: string, inputElId: string, layerType: "Address" | "LGA") {
        this.rootEl = document.getElementById(rootElId) as HTMLDivElement
        this.inputEl = document.getElementById(inputElId) as HTMLInputElement;
        this.autocompleteInputEl = document.getElementById(autocompleteInputElId) as HTMLInputElement
        this.suggestionsList = this.rootEl.querySelector('.suggestions-list') as HTMLUListElement;
        this.addressFields = {
            StreetAddress: this.rootEl.querySelector("#StreetAddress") as HTMLInputElement,
            StreetAddressLine2: this.rootEl.querySelector("#StreetAddressLine2") as HTMLInputElement,
            City: this.rootEl.querySelector("#City") as HTMLInputElement,
            State: this.rootEl.querySelector("#State") as HTMLInputElement,
            Postcode: this.rootEl.querySelector("#Postcode") as HTMLInputElement,
        };
        this.layerType = layerType
        this.hoveredIndex = -1;
        this.selectedAddress = {
            Street: "",
            Street2: "",
            Suburb: "",
            Postcode: "",
            City: "",
            State: "",
            Country: "",
            LocalGovernment: {
                Code: "",
                Name: ""
            },
            Type: "",
        };
        this.Init();
    }

    Init() {
        //Initialize event listeners
        this.CreateEventListeners();
        if (this.layerType == "Address") {
            this.PopulateValues()
        } else {
            this.PopulateLGAValue();
        }
    }
    async CreateEventListeners() {
        // Handle keyboard navigation
        this.autocompleteInputEl.addEventListener('keydown', await this.OnKeyDown.bind(this));
        document.addEventListener('click', this.OnDocumentClick.bind(this));

        // Handle suggestion clicks
        if (this.suggestionsList)
            this.suggestionsList.addEventListener('click', await this.OnSuggestionClick.bind(this))

        // Handle input changes
        if (this.autocompleteInputEl)
            this.autocompleteInputEl.addEventListener('input', await this.OnInput.bind(this));

        if (this.addressFields) {
            Object.values(this.addressFields).forEach((fieldInput: HTMLInputElement) => {
                fieldInput?.addEventListener('input', this.OnFieldsValueChange.bind(this))
            });
        }
    }

    PopulateValues() {
        const value = this.inputEl.value;
        if (value) {
            const address: Address = JSON.parse(value);
            this.SetDependantFields(address);
        }
    }

    async PopulateLGAValue() {
        const value = this.inputEl.value;
        if (value) {
            await this.SendTimeoutRequest(value);
            setTimeout(() => {
                const selectedItem = this.suggestionsList.querySelector<HTMLLIElement>(`li[data-contactemail="${value}"]`);
                if (selectedItem) {
                    this.autocompleteInputEl.value = selectedItem.textContent;
                    this.autocompleteInputEl.setAttribute('aria-expanded', 'false');
                }
            }, 1);
        }
    }
    async OnSuggestionClick(e: MouseEvent) {
        const suggestion: HTMLLIElement = e.target as HTMLLIElement;
        if (suggestion) {
            await this.SelectSuggestion(suggestion);
        }
    }

    OnDocumentClick(e: MouseEvent) {
        if (e.target !== this.autocompleteInputEl &&
            e.target !== this.suggestionsList &&
            !this.suggestionsList.contains(e.target as Node)
        ) {
            this.HideSuggestions();
        }
    }

    async OnInput() {
        const query = this.autocompleteInputEl.value;
        clearTimeout(this.timeoutId);

        this.timeoutId = setTimeout(() => { this.SendTimeoutRequest(query) }, 500);
    }

    OnFieldsValueChange(e: Event) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
        const target = e.target as HTMLInputElement;
        const targetId = target.id;
        switch (targetId) {
            case "StreetAddress":
                this.selectedAddress["Street"] = target.value;
                break;
            case "StreetAddressLine2":
                this.selectedAddress["Street2"] = target.value;
                break;
            case "City":
                this.selectedAddress["City"] = target.value;
                break;
            case "State":
                this.selectedAddress["State"] = target.value;
                break;
            case "Postcode":
                this.selectedAddress["Postcode"] = target.value;
                break;
        }
        this.inputEl.value = this.setJsonUnlessEmpty();
        this.inputEl.dispatchEvent(new Event('change'));
    }

    setJsonUnlessEmpty(): string {
        if (isEmpty(this.selectedAddress.City) &&
            isEmpty(this.selectedAddress.Country) && 
            isEmpty(this.selectedAddress.LocalGovernment.Code) && 
            isEmpty(this.selectedAddress.LocalGovernment.Name) && 
            isEmpty(this.selectedAddress.Postcode) && 
            isEmpty(this.selectedAddress.State) && 
            isEmpty(this.selectedAddress.Street) && 
            isEmpty(this.selectedAddress.Suburb) && 
            isEmpty(this.selectedAddress.Street2) 
        )
            return ""; // Set input to empty string to trigger validation

        return JSON.stringify(this.selectedAddress);
    }

    async SendTimeoutRequest(query: string) {
        const currentQuery = this.autocompleteInputEl.value;
        let suggestions: AddressSuggestion[] | LocalGovernmentArea[] = null;

        // Fetch address suggestions
        if (this.layerType == "LGA") {
            suggestions = await this.GetLGASuggestions(query);
            if (suggestions)
                this.PopulateAddressSuggestions(suggestions);
        } else {
            if (currentQuery != query) // Ensure query is not already requested
                return;
            suggestions = await this.GetAddressSuggestions(query);
            this.PopulateAddressSuggestions(suggestions);
        }
    }

    async OnKeyDown(e: KeyboardEvent) {

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const suggestions = this.suggestionsList.querySelectorAll('li');

            if (suggestions.length > 0) {
                if (e.key === 'ArrowDown') {
                    this.hoveredIndex = Math.min(this.hoveredIndex + 1, suggestions.length - 1);
                } else {
                    this.hoveredIndex = Math.max(this.hoveredIndex - 1, 0);
                }
                suggestions.forEach((suggestion, index) => {
                    suggestion.classList.toggle('active', index === this.hoveredIndex);
                });
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();

            const suggestion: HTMLLIElement = this.suggestionsList.querySelector('li.active') as HTMLLIElement;
            if (suggestion) {
                await this.SelectSuggestion(suggestion);
            }

        } else if (e.key === 'Tab') {

            const suggestion: HTMLLIElement = this.suggestionsList.querySelector('li.active') as HTMLLIElement;
            if (suggestion) {
                await this.SelectSuggestion(suggestion);
                e.preventDefault();
            } else {
                const suggestions = this.suggestionsList.querySelectorAll('li');
                if (suggestions.length > 0) this.HideSuggestions();
            }
        }
    }
    async GetAddress(query: string): Promise<any> {
        try {
            const response = await fetch('/arcgis/select', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(query)
            });

            if (!response.ok) {
                throw new Error('Failed to fetch suggestions');
            }

            const address = await response.json();
            return address;
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            return [];
        }
    }

    async GetAddressSuggestions(query: string): Promise<AddressSuggestion[]> {
        try {
            const response = await fetch('/arcgis/addressautosuggest', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(query)
            });

            if (!response.ok) {
                throw new Error('Failed to fetch suggestions');
            }

            const suggestions = await response.json();
            return suggestions;
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            return [];
        }
    }

    async GetLGASuggestions(query: string): Promise<LocalGovernmentArea[]> {
        try {
            const response = await axios.post('/arcgis/lgaautosuggest', query, {
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (response.status != 200) {
                throw new Error('Failed to fetch suggestions');
            }
            return response.data;

        } catch (error) {
            console.error('Error fetching suggestions:', error);
            return null;
        }
    }

    PopulateAddressSuggestions<T extends AddressSuggestion | LocalGovernmentArea>(suggestions: T[]) {
        this.suggestionsList.classList.add("show")
        this.suggestionsList.setAttribute("role", "listbox");
        this.suggestionsList.innerHTML = '';
        if (this.layerType == "LGA") {
            suggestions.forEach((suggestion) => {
                const suggestionObj = suggestion as LocalGovernmentArea;
                if (suggestionObj) {
                    const listItem = document.createElement('li');
                    listItem.textContent = suggestionObj.lgaName;
                    listItem.setAttribute("data-lgacode", suggestionObj.lgaCode);
                    listItem.setAttribute("data-contactemail", suggestionObj.contactEmail);
                    listItem.setAttribute("role", "option");
                    listItem.setAttribute("aria-selected", "false");
                    listItem.classList.add('dropdown-item');
                    this.suggestionsList.appendChild(listItem);
                }
            });
        }
        else if (Array.isArray(suggestions) && suggestions.length > 0) {

            //Loop through new suggestions and populate dropdown list
            suggestions.forEach(suggestion => {
                const suggestionObj = suggestion as AddressSuggestion;
                if (suggestionObj) {
                    const listItem = document.createElement('li');
                    listItem.textContent = suggestionObj.text;
                    listItem.classList.add('dropdown-item');
                    listItem.setAttribute("role", "option");
                    listItem.setAttribute("aria-selected", "false");
                    this.suggestionsList.appendChild(listItem);
                }
            });
        }
        else {
            this.ResetValues()
        }
        this.autocompleteInputEl.setAttribute('aria-expanded', 'true');
    }
    async SelectSuggestion(suggestion: HTMLLIElement) {
        //Fetch actual location from ArcGIS API
        let arcgisAddress: SelectedResult

        if (this.layerType == "Address") {
            arcgisAddress = await this.GetAddress(suggestion.textContent);
            this.selectedAddress = this.AddressMapper(arcgisAddress);
            if (arcgisAddress) {
                //Map it to JSON object, save in hidden form component and clear the suggestion list
                this.inputEl.value = JSON.stringify(this.selectedAddress);
                this.inputEl.dispatchEvent(new Event('change'));

                this.autocompleteInputEl.value = suggestion.textContent;
                this.suggestionsList.classList.remove("show")
                this.suggestionsList.innerHTML = '';

                this.SetDependantFields(this.selectedAddress)
            }
        }
        else {
            const value = suggestion.getAttribute("data-contactemail");
            this.inputEl.value = value;
            this.inputEl.dispatchEvent(new Event('change'));

            this.autocompleteInputEl.value = suggestion.textContent;
            this.suggestionsList.classList.remove("show")
            this.suggestionsList.innerHTML = '';
        }
    }

    SetDependantFields(address: Address) {
        if (this.addressFields['StreetAddress'])
            this.addressFields['StreetAddress'].value = this.GetValue(address.Street) + ", " + this.GetValue(address.Suburb);

        if (this.addressFields['City'])
            this.addressFields['City'].value = this.GetValue(address.City);

        if (this.addressFields['State'])
            this.addressFields['State'].value = this.GetValue(address.State);

        if (this.addressFields['Postcode'])
            this.addressFields['Postcode'].value = this.GetValue(address.Postcode);

        const addressElements = [address.Street, address.Street2, address.City, address.State, address.Postcode, address.Country].filter(add => add != null && add.trim() !== "");
        this.autocompleteInputEl.textContent = addressElements.join(", ");
        this.autocompleteInputEl.value = addressElements.join(", ");
        this.fieldsPopulated = true;
    }

    ResetValues() {
        this.inputEl.value = "";
        this.inputEl.dispatchEvent(new Event('change'));

        this.HideSuggestions();
        const address: Address = {
            Type: "",
            Street: "",
            Suburb: "",
            City: "",
            State: "",
            Postcode: "",
            Country: "",
            LocalGovernment: {
                Code: "",
                Name: ""
            },
        }
        this.SetDependantFields(address)
    }

    HideSuggestions() {
        if (this.suggestionsList.classList.contains("show"))
            this.suggestionsList.classList.remove("show")
        this.suggestionsList.innerHTML = '';
    }
    // Maps retrieved ArcGIS response data to a structured address object with specific properties.
    AddressMapper(selectedResult: SelectedResult) {
        const addressAttributes = selectedResult?.GeocodeResult?.attributes
        const lgaAttributes = selectedResult?.LocalGovernmentAuthority?.features[0].attributes

        return {
            Type: this.layerType,
            Name: addressAttributes?.AddBldg != "" ? addressAttributes?.AddBldg
                : addressAttributes?.PlaceName != "" ? addressAttributes?.PlaceName
                    : addressAttributes?.ExInfo != "" ? this.ToTitleCase(addressAttributes?.ExInfo)
                        : "" ?? "",
            Street: addressAttributes?.StAddr ?? "",
            Suburb: addressAttributes?.Nbrhd ? addressAttributes?.Nbrhd : addressAttributes?.City ?? "",
            City: addressAttributes?.City ?? "",
            State: addressAttributes?.Region ?? "",
            Postcode: addressAttributes?.Postal ?? "",
            Country: addressAttributes?.Country ?? "",
            LocalGovernment: {
                Code: String(lgaAttributes?.abs_lga_nu) ?? "",
                Name: lgaAttributes?.name ?? ""
            },
            UserId: this.userId
        }
    }


    GetQueryStringParam(param: string) {
        const queryParams = new URLSearchParams(window.location.search);
        return queryParams.get(param);
    }
    GetValue(field: string) {
        if (field == "null" || field == "" || field == "undefined")
            return "";
        else
            return field;
    }
    ToTitleCase(str: string) {
        return str
            .toLowerCase() // Convert the entire string to lowercase first
            .split(' ') // Split the string into words
            .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
            .join(' '); // Join the words back into a single string
    }
}
